import React, { useState, ChangeEvent, useRef } from 'react';
import { saveDish, uploadDishImage } from './slice';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { AddDishContainer, AddDishTitle, AddDishContentHeader, AddDishContentBody } from './Menu.styled';
import { DishRequest, Dish } from './types';
import { selectCurrentRestaurant, setOpen, selectShouldOpen, selectMessage, selectSeverity } from '../Restaurant/slice';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useParams } from 'react-router-dom';
import PageContainer, { BreadCrumbLink } from '../../components/PageContainer/PageContainer';
import { withAuthenticationRequired } from '@auth0/auth0-react';

const AddDish: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [dietary, setDietary] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { alias, menuID } = useParams();
  const restaurant = useAppSelector(selectCurrentRestaurant);

  const menu = restaurant?.menus?.find(m => m.menuID === menuID);
  const breadcrumbLinks: BreadCrumbLink[] = [
    {
      name: restaurant?.name ?? '',
      url: `/restaurant/${alias}`
    },
    {
      name: menu?.name ?? menuID ?? '',
      url: `/restaurant/${alias}/menu/${menuID}`
    }
  ];

  const openServer = useAppSelector(selectShouldOpen);
  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const setOpenServer = (open: boolean): void => {
    dispatch(setOpen(open));
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file != null) {
      if (file.type.startsWith('image/')) {
        setImageFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleClearImage = (): void => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current != null) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveDish = async (): Promise<void> => {
    // Prevent multiple submissions - early return if already saving
    if (isSaving || isUploadingImage) {
      return;
    }
    
    setIsSaving(true);
    const dishRequest: DishRequest = {
      menuId: menuID ?? '',
      dishId: '',
      name,
      price: Number(price),
      description,
      dietary
    };
    try {
      const result = await dispatch(saveDish(dishRequest)).unwrap();
      // Extract dishId from response - the backend returns the updated restaurant with dishes
      // Find the newly created dish by matching name and description (most recent dish with matching fields)
      const savedDish = (result.data as any)?.menus
        ?.flatMap((m: any) => m.dishes ?? [])
        ?.filter((d: Dish) => d.name === name && d.description === description)
        ?.sort((a: Dish, b: Dish) => {
          // Sort by created date if available, otherwise take the last one
          if (a.created && b.created) {
            return new Date(b.created).getTime() - new Date(a.created).getTime();
          }
          return 0;
        })?.[0];
      
      const dishId = savedDish?.dishID;
      
      // Upload image if one was selected
      if (imageFile != null && dishId != null) {
        setIsUploadingImage(true);
        try {
          const uploadResult = await dispatch(uploadDishImage({ dishId, file: imageFile })).unwrap();
          // Update the dish with the uploaded image URL in the save call
          const updateRequest: DishRequest = {
            menuId: menuID ?? '',
            dishId,
            name,
            price: Number(price),
            description,
            dietary,
            imageUrl: uploadResult.imageUrl
          };
          await dispatch(saveDish(updateRequest));
        } catch (error) {
          console.error('Failed to upload dish image:', error);
        } finally {
          setIsUploadingImage(false);
        }
      }
      
      setName('');
      setDescription('');
      setPrice('');
      setDietary('');
      setImageFile(null);
      setImagePreview(null);
      navigate(`/restaurant/${alias}/menu/${menuID}`);
    } catch (error) {
      console.error('Failed to save dish:', error);
      // Error is already handled by Redux slice, but we ensure isSaving is reset
    } finally {
      setIsSaving(false);
    }
  };
    
  return (
    <PageContainer
      title="Add Dish"
      breadcrumbs={breadcrumbLinks}
      open={openServer}
      setOpen={setOpenServer}
      message={message}
      severity={severity}
    >
      <AddDishContainer>
        <AddDishContentHeader>
          <AddDishTitle>Add Dish</AddDishTitle>
        </AddDishContentHeader>
        <AddDishContentBody>
          <TextField
            fullWidth
            label="Dish Name"
            variant="outlined"
            value={name}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value);
            }}
            size="small"
          />
          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            multiline
            rows={3}
            value={description}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setDescription(event.target.value);
            }}
          />
          <TextField
            fullWidth
            label="Price"
            variant="outlined"
            type="number"
            inputProps={{ step: '0.01' }}
            value={price}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setPrice(event.target.value);
            }}
            size="small"
          />
          <TextField
            fullWidth
            label="Dietary"
            variant="outlined"
            value={dietary}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setDietary(event.target.value);
            }}
            size="small"
          />
          
          {/* Dish Image Upload Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                fileInputRef.current?.click();
              }}
              disabled={isSaving || isUploadingImage}
            >
              {imageFile != null ? 'Change Image' : 'Select Dish Image (optional)'}
            </Button>
            {imagePreview != null && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                <Avatar
                  src={imagePreview}
                  alt="Dish preview"
                  sx={{ width: 150, height: 150, alignSelf: 'flex-start' }}
                  variant="rounded"
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleClearImage}
                  sx={{ alignSelf: 'flex-start' }}
                  disabled={isSaving || isUploadingImage}
                >
                  Remove Image
                </Button>
              </Box>
            )}
            {isUploadingImage && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CircularProgress size={16} />
                <span style={{ fontSize: '14px', color: '#666' }}>Uploading image...</span>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                void handleSaveDish();
              }}
              disabled={isSaving || isUploadingImage}
              startIcon={isSaving || isUploadingImage ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
            >
              {isSaving ? 'Saving...' : isUploadingImage ? 'Uploading...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                navigate(`/restaurant/${alias}/menu/${menuID}`);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </Box>
        </AddDishContentBody>
      </AddDishContainer>
    </PageContainer>
  );
}

export default withAuthenticationRequired(AddDish)
